import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useMediaQuery } from '@mui/material';
import ProductImages from '../../components/ProductDetails/ProductImages';
import ProductDetailsTabView from '../../components/ProductDetailsTabView';
import CustomLoading from '../../components/customLoading/CustomLoading';
import image404 from '../../assets/images/notfount.webp';
import { getProductDetails, patchProductDetails, patchProductDetailsFormData } from '../../services/products';
import Dropdown from '../../components/inputFields/DropDown';
import InputField from '../../components/inputFields/InputField';
import TextField from '../../components/inputFields/TextField';
import Button from '../../components/buttons/Button';
import Breadcrumbs from '../../components/BreadCrumbs';
import { TiDeleteOutline } from 'react-icons/ti';
import { CgAdd } from 'react-icons/cg';
import TextareaField from '../../components/inputFields/TextField';
import FormikMultiAsyncDropdown from '../../components/inputFields/FormikMultiAsyncDropdown';
import { searchProduct } from '../../services/search';

const statusOptions = [
    { value: 'IN_STOCK', label: 'In Stock' },
    { value: 'OUT_OF_STOCK', label: 'Out of Stock' },
    { value: 'LIMITED_STOCK', label: 'Limited Stock' },
    { value: 'PREORDER', label: 'Preorder' },
    { value: 'DISCONTINUED', label: 'Discontinued' },
];

const ProductDetailedPage = () => {
    const { slug } = useParams();
    const isTab = useMediaQuery('(max-width: 966px)');
    const [productData, setProductData] = useState(null);
    const [productFound, setProductFound] = useState(true);
    const [images, setImages] = useState([]);
    const [imagesToRemove, setImagesToRemove] = useState([]);
    const [videos, setVideos] = useState([]);
    const [videosToRemove, setVideosToRemove] = useState([]);
    const [lang, setLang] = useState('en');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setProductData(null);
                const response = await getProductDetails(slug, lang);
                setProductData(response.data);
                const imgs = (response.data?.product_images || []).map((img) => ({
                    id: img.id ?? `${Date.now()}_${Math.random()}`,
                    url: img.image,
                }));
                setImages(imgs);
                setImagesToRemove([]);
                const vids = (response.data?.product_videos || []).map((vid) => ({
                    id: vid.id ?? `${Date.now()}_${Math.random()}`,
                    url: typeof vid === 'string' ? vid : vid.video || vid.url,
                }));
                setVideos(vids);
                setVideosToRemove([]);
                setProductFound(true);
            } catch (err) {
                setProductFound(false);
            }
        };
        fetchProduct();
    }, [slug, lang]);

    const initialValues = productData ? {
        meta_description: productData.meta_description,
        meta_keywords: productData.meta_keywords,
        availability_status: productData.availability_status,
        mrp: productData.mrp,
        discount_percentage: productData.discount_percentage,
        product_name: productData.product_name,
        description: productData.description,
        similar_products: productData.related_products.map((it) => ({
            value: it.id,
            label: it.product_name,
        })),
        product_details: {
            FEATURES: productData.product_details?.FEATURES || [],
            TECHNICAL_DETAILS: productData.product_details?.TECHNICAL_DETAILS || [],
            BENEFITS: productData.product_details?.BENEFITS || [],
            USECASE: productData.product_details?.USECASE || [],
            IN_BOX: productData.product_details?.IN_BOX || [],
            ADDITIONAL_DETAILS: productData.product_details?.ADDITIONAL_DETAILS || [],
        },
    } : {};

    const validationSchema = Yup.object({
        availability_status: Yup.string().required('Required'),
        mrp: Yup.number().required('Required'),
        discount_percentage: Yup.number().required('Required'),
        product_name: Yup.string().required('Required'),
    });

    const calculatePrice = (mrp, discount) => {
        return Math.round(mrp - (mrp * discount / 100));
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            const hasImageChanges = imagesToRemove.length > 0 || images.some((it) => it.file);
            const hasVideoChanges = videosToRemove.length > 0 || videos.some((it) => it.file);
            const simProducts = [];
            values.similar_products.forEach((it) => {
                simProducts.push(it.value);
            });
            let res;
            if (hasImageChanges || hasVideoChanges) {
                const data = new FormData();
                data.append('availability_status', values.availability_status);
                data.append('mrp', values.mrp);
                data.append('discount_percentage', values.discount_percentage);
                data.append('product_name', values.product_name);
                data.append('description', values.description || '');
                data.append('product_details', JSON.stringify(values.product_details || {}));
                data.append('meta_description', values.meta_description);
                data.append('meta_keywords', values.meta_keywords);
                if (imagesToRemove.length > 0) {
                    data.append('images_to_delete', imagesToRemove);
                }
                if (videosToRemove.length > 0) {
                    videosToRemove.forEach((videoId) => {
                        data.append('product_video_ids_to_delete', videoId);
                    });
                }
                images.forEach((img) => {
                    if (img.file && typeof img.file === 'object') {
                        data.append('images', img.file);
                    }
                });
                videos.forEach((vid) => {
                    if (vid.file && typeof vid.file === 'object') {
                        data.append('videos', vid.file);
                        // If video is being replaced (has both url and file), delete the old one
                        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                        if (vid.id && uuidPattern.test(vid.id) && vid.url && !videosToRemove.includes(vid.id)) {
                            data.append('product_video_ids_to_delete', vid.id);
                        }
                    }
                });
                res = await patchProductDetailsFormData({ id: productData.id, data });
            } else {
                res = await patchProductDetails({ id: productData.id, data: { ...values, similar_products: simProducts } });
            }
            if (res.status === 200) {
                toast.success("Product Updated Successfully");
                // Refresh product details to reflect any server-side changes
                try {
                    const refreshed = await getProductDetails(slug, lang);
                    setProductData(refreshed.data);
                    const imgs = (refreshed.data?.product_images || []).map((img) => ({
                        id: img.id ?? `${Date.now()}_${Math.random()}`,
                        url: img.image,
                    }));
                    setImages(imgs);
                    setImagesToRemove([]);
                    const vids = (refreshed.data?.product_videos || []).map((vid) => ({
                        id: vid.id ?? `${Date.now()}_${Math.random()}`,
                        url: typeof vid === 'string' ? vid : vid.video || vid.url,
                    }));
                    setVideos(vids);
                    setVideosToRemove([]);
                    navigate(-1);
                } catch (_) { }
                setSubmitting(false);
            } else {
                toast.error("Unable to update Product");
                setSubmitting(false);
            }
        } catch (error) {
            console.error("Error in updating details:", error);
            toast.error("Unable to update Product");
            setSubmitting(false);
        }
    };

    const replaceImageAt = (index, file) => {
        setImages((prev) => prev.map((img, i) => (i === index ? { ...img, file, preview: URL.createObjectURL(file) } : img)));
    };

    const addImage = (file) => {
        setImages((prev) => [...prev, { id: `${Date.now()}_${Math.random()}`, file, preview: URL.createObjectURL(file) }]);
    };

    const removeImageAt = (img) => {
        let imageIdentifier = (typeof img.url === 'string' ? img.url.split('/').pop() : undefined);
        if (imageIdentifier) {
            setImagesToRemove((del) => (del.includes(imageIdentifier) ? del : [...del, imageIdentifier]));
        }
        setImages((prev) => prev.filter((it) => it.id !== img.id));
    };

    const replaceVideoAt = (index, file) => {
        setVideos((prev) => prev.map((vid, i) => (i === index ? { ...vid, file, preview: URL.createObjectURL(file) } : vid)));
    };

    const addVideo = (file) => {
        setVideos((prev) => [...prev, { id: `${Date.now()}_${Math.random()}`, file, preview: URL.createObjectURL(file) }]);
    };

    const removeVideoAt = (vid) => {
        // Use the video ID (UUID) from the API response for deletion
        // UUID pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        // Only add to remove list if it's an existing video (has UUID from API, not a newly generated ID)
        if (vid.id && uuidPattern.test(vid.id) && !vid.file) {
            setVideosToRemove((del) => (del.includes(vid.id) ? del : [...del, vid.id]));
        }
        setVideos((prev) => prev.filter((it) => it.id !== vid.id));
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Bazaar', },
            { name: 'Products', link: (-1) },
            { name: "Product Details" },
        ]} />
        <div className="flex justify-end mt-2">
            <div className="w-40">
                <Dropdown
                    name="language"
                    value={lang}
                    options={[
                        { value: 'en', label: 'English' },
                        { value: 'hi', label: 'हिन्दी' },
                    ]}
                    placeholder="Language"
                    onChange={(val) => setLang(val)}
                />
            </div>
        </div>
        <div className="bg-gray-50">
            {productFound ? (
                productData ? (
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue }) => (
                            <Form className="p-4 md:p-8">
                                <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                    <div className="flex flex-col shadow-lg p-4 rounded-lg border bg-white">
                                        <h2 className="text-lg font-semibold mb-2">Images & Videos</h2>
                                        <ProductImages
                                            isTab={isTab}
                                            productData={productData}
                                            editable
                                            images={images}
                                            videos={videos}
                                            onReplaceImage={replaceImageAt}
                                            onAddImage={addImage}
                                            onRemoveImage={removeImageAt}
                                            onReplaceVideo={replaceVideoAt}
                                            onAddVideo={addVideo}
                                            onRemoveVideo={removeVideoAt}
                                        />

                                        <div className="mt-4">
                                            <Dropdown
                                                name="availability_status"
                                                label="Availability Status"
                                                value={values.availability_status}
                                                onChange={(val) => setFieldValue('availability_status', val)}
                                                options={statusOptions}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col shadow-lg p-4 rounded-lg border bg-white">
                                        <h2 className="text-lg font-semibold mb-2">Basic Details</h2>
                                        <div className="grid grid-cols-1 gap-4">
                                            <InputField
                                                name="product_name"
                                                type="text"
                                                label="Product Name"
                                                required
                                            />
                                            <p className='text-sm text-gray-500'>{values.manufacturer}</p>
                                        </div>

                                        <div className="mt-4">
                                            <h3 className="text-md font-semibold mb-2">Key Features</h3>
                                            <FieldArray name="product_details.FEATURES">
                                                {({ push, remove }) => (
                                                    <div className="space-y-2">
                                                        {(values.product_details?.FEATURES || []).map((_, index) => (
                                                            <div key={index} className="flex items-center gap-2">
                                                                <span className="checkmark mr-2 bg-primary rounded-full">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                        <path d="M5 10l3 3L15 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                </span>
                                                                <InputField
                                                                    name={`product_details.FEATURES[${index}].value`}
                                                                    type="text"
                                                                    placeholder="Enter feature"
                                                                    className="flex-1"
                                                                />
                                                                <button type="button" onClick={() => remove(index)} className="p-2 text-red-500 hover:text-red-700">
                                                                    <TiDeleteOutline size={20} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            onClick={() => push({ value: '' })}
                                                            className="inline-flex items-center gap-2 text-primary hover:text-blue-700 mt-2"
                                                        >
                                                            <CgAdd size={18} /> Add Feature
                                                        </button>
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>
                                    </div>

                                    <div className="flex flex-col shadow-lg p-4 rounded-lg border bg-white">
                                        <h2 className="text-lg font-semibold mb-4 text-center">Edit Product Pricing</h2>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <InputField
                                                name="mrp"
                                                type="number"
                                                label="Maximum Retail Price (MRP)"
                                                required
                                            />
                                            <InputField
                                                name="discount_percentage"
                                                type="number"
                                                label="Discount Percentage (%)"
                                                required
                                            />
                                        </div>
                                        <div className="flex justify-end mt-4">
                                            <Button
                                                type="submit"
                                                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors w-full"
                                                children={"Update Product"}
                                            />
                                        </div>

                                        <div className="bg-gray-100 p-3 rounded-md text-center mt-4">
                                            <span className="block text-sm text-gray-600 mb-1">Calculated Price</span>
                                            <span className="text-xl font-bold text-primary">
                                                ₹{calculatePrice(values.mrp, values.discount_percentage)}
                                            </span>
                                            <p className="text-xs text-gray-500 mt-1">Inclusive of all taxes</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 mb-4">
                                        <TextareaField
                                            name="meta_description"
                                            type="text"
                                            label="Meta Description"
                                            placeholder="Enter meta description"
                                        />
                                        <TextareaField
                                            name="meta_keywords"
                                            type="text"
                                            label="Meta Keywords"
                                            placeholder="Enter meta keywords (Comma separated)"
                                        />
                                    </div>
                                </div>

                                <div className='my-2 mt-4'>
                                    <TextField name="description" label="Description" />
                                </div>
                                <FormikMultiAsyncDropdown
                                    name="similar_products"
                                    label="Similar Products"
                                    needSearch
                                    apiCallFunction={async (input) => {
                                        const response = await searchProduct(input);
                                        return response.data.results.filter((cat) => cat.id !== productData.id).map((cat) => ({
                                            value: cat.id,
                                            label: cat.product_name,
                                        }));
                                    }}
                                    placeholder="Search product..."
                                />
                                <ProductDetailsTabView productDetails={values.product_details} editable />
                            </Form>
                        )}
                    </Formik>
                ) : (
                    <div className="h-full w-full flex justify-center">
                        <CustomLoading />
                    </div>
                )
            ) : (
                <div className="container mx-auto mt-32 flex flex-col justify-center items-center">
                    <img src={image404} alt="404 Page Not Found" />
                    <span className='font-bold text-md text-center sm:text-xl md:text-4xl mb-10 text-primary'>
                        Looks like this is an invalid product
                    </span>
                </div>
            )}
        </div>
    </>
    );
};

export default ProductDetailedPage;
